import {type HelpCenterArticle} from './types';

const TITLE_WEIGHT = 5;
const DESCRIPTION_WEIGHT = 3;
const BODY_WEIGHT = 1;

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'can',
  'do',
  'does',
  'for',
  'from',
  'how',
  'i',
  'if',
  'in',
  'is',
  'it',
  'me',
  'my',
  'of',
  'on',
  'or',
  'the',
  'to',
  'was',
  'what',
  'when',
  'where',
  'why',
  'will',
  'with',
  'you',
  'your',
]);

const normalize = (value: string): string => value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ');

const toWords = (value: string): string[] => normalize(value).split(/\s+/).filter(Boolean);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const hasPrefix = (words: string[], token: string): boolean =>
  words.some(word => word.startsWith(token));

export const tokenize = (query: string): string[] => {
  const all = toWords(query);
  const meaningful = all.filter(token => !STOP_WORDS.has(token));
  return meaningful.length > 0 ? meaningful : all;
};

export const searchArticles = (
  articles: HelpCenterArticle[],
  query: string,
): HelpCenterArticle[] => {
  const tokens = tokenize(query);
  if (tokens.length === 0) return [];

  const haystacks = articles.map(article => ({
    title: toWords(article.title),
    description: toWords(article.description),
    body: toWords(article.searchText),
  }));

  const rarity = new Map<string, number>();
  tokens.forEach(token => {
    const hits = haystacks.filter(
      h => hasPrefix(h.title, token) || hasPrefix(h.description, token) || hasPrefix(h.body, token),
    ).length;
    rarity.set(token, Math.log((articles.length + 1) / (hits + 1)) + 1);
  });

  return articles
    .map((article, index) => {
      const {title, description, body} = haystacks[index];

      let score = 0;
      for (const token of tokens) {
        const inTitle = hasPrefix(title, token);
        const inDescription = hasPrefix(description, token);
        const inBody = hasPrefix(body, token);
        if (!inTitle && !inDescription && !inBody) return null;
        let fieldWeight = BODY_WEIGHT;
        if (inTitle) {
          fieldWeight = TITLE_WEIGHT;
        } else if (inDescription) {
          fieldWeight = DESCRIPTION_WEIGHT;
        }
        score += fieldWeight * (rarity.get(token) ?? 1);
      }
      return {article, score};
    })
    .filter((match): match is {article: HelpCenterArticle; score: number} => match !== null)
    .sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title))
    .map(match => match.article);
};

/** Index of the first query match in `text`, or -1. */
export const firstMatchIndex = (text: string, query: string): number => {
  const tokens = tokenize(query);
  if (tokens.length === 0) return -1;

  return text.search(new RegExp(`\\b(?:${tokens.map(escapeRegExp).join('|')})\\w*`, 'i'));
};

export type TextSegment = {text: string; matched: boolean};

export const splitOnMatches = (text: string, query: string): TextSegment[] => {
  const tokens = tokenize(query);
  if (tokens.length === 0) return [{text, matched: false}];

  const pattern = new RegExp(`\\b(?:${tokens.map(escapeRegExp).join('|')})\\w*`, 'gi');
  const segments: TextSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(pattern)) {
    if (match.index > cursor) {
      segments.push({text: text.slice(cursor, match.index), matched: false});
    }
    segments.push({text: match[0], matched: true});
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) segments.push({text: text.slice(cursor), matched: false});
  return segments;
};
